// Street Interview Ad's draft as the form keeps it (spec G14-street-interview-ad, G14 PR 17): the
// server holds the member's draft (`.../templates/street-interview-ad/draft`), so it survives the
// panel; the form reads it, keeps its edits there, and asks the server's pack reader, directors
// and stills (live's `useStreetInterviewDraft`, which ran them from the browser).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetStreetInterviewAdDraftQueryKey,
  useApproveStreetInterviewAdCast,
  useApproveStreetInterviewAdScript,
  useGetStreetInterviewAdDraft,
  useRegenerateStreetInterviewAdStill,
  useResetStreetInterviewAdDraft,
  useWriteStreetInterviewAdDraft,
  useWriteStreetInterviewAdScript,
  writeStreetInterviewAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  StreetDraftStill,
  StreetDraftStillName,
  StreetInterviewAdDraft,
  StreetScript,
  TemplateExample,
  VideoCaptionPosition,
} from "../../../api/generated/model";
import {
  StreetInterviewAdApproval,
  StreetInterviewAdDraftWrite,
  StreetInterviewAdInputs,
  StreetInterviewAdScriptAsk,
  type StreetInterviewAdDraftWriteOutput,
  type StreetInterviewAdInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface DraftForm {
  inputs: StreetInterviewAdInputsOutput;
  script: StreetScript | null;
}

/** The cast's six stills, then the keyframes in the order their clips play (live's
 * `CAST_STILLS`, `KEYFRAME_STILLS`), and what each is drawn from (`STILL_GRAPH.sources`). */
export const CAST_STILLS: readonly StreetDraftStillName[] = [
  "street",
  "sheet_int",
  "sheet_p1",
  "sheet_p2",
  "sheet_p3",
  "sheet_p4",
];
export const KEYFRAME_STILLS: readonly StreetDraftStillName[] = [
  "two_1",
  "single_2",
  "single_3",
  "single_4",
  "handover_3",
  "hold_2",
  "hold_1",
  "two_3_hold",
  "hold_3",
  "signoff",
  "end_card",
];
const STILL_SOURCES: Readonly<Partial<Record<StreetDraftStillName, readonly StreetDraftStillName[]>>> = {
  sheet_int: ["street"],
  sheet_p1: ["street"],
  sheet_p2: ["street"],
  sheet_p3: ["street"],
  sheet_p4: ["street"],
  two_1: ["street", "sheet_int", "sheet_p1"],
  single_2: ["street", "sheet_p2", "sheet_int"],
  single_3: ["street", "sheet_p3", "sheet_int"],
  single_4: ["street", "sheet_p4", "sheet_int"],
  handover_3: ["street", "sheet_int", "sheet_p3"],
  two_3_hold: ["handover_3"],
  hold_1: ["two_1"],
  hold_2: ["single_2"],
  hold_3: ["single_3"],
  signoff: ["sheet_int", "street"],
};

/** The draft refetched while it works: every 2 s while a director or a still works, and a moment
 * after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** The video's settings, editable after the approvals as live's footer keeps them (Subtitles and
 * the Title): the form's own until "Generate final video" sends them. */
export interface VideoSettings {
  subtitles: boolean;
  caption_position: VideoCaptionPosition;
  caption_style_id: string;
  title: boolean;
}

/** "Generate final video"'s body (live's hand-off): the approved script and spec, the eleven
 * keyframes by reference and the form's captions and title; null until all 17 are drawn. */
export function videoBody(draft: StreetInterviewAdDraft, settings: VideoSettings) {
  if (draft.stage !== "keyframes" || draft.script === null || draft.spec === null) return null;
  const drawn = Object.fromEntries(
    draft.stills.flatMap((each) => (each.output ? [[each.name, each.output] as const] : [])),
  );
  if (!CAST_STILLS.every((name) => name in drawn)) return null;
  if (!KEYFRAME_STILLS.every((name) => name in drawn)) return null;
  const stills = Object.fromEntries(KEYFRAME_STILLS.map((name) => [name, drawn[name]]));
  return {
    type: "video.template" as const,
    inputs: { template: "street-interview-ad" as const, script: draft.script, spec: draft.spec, stills, ...settings },
  };
}

/** A still under way (live's `isStillWaiting`): drawing, or pending on stills that are all done
 * or still coming in its stage. */
export function stillWaiting(name: StreetDraftStillName, stills: readonly StreetDraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const sources = STILL_SOURCES[name] ?? [];
  return (
    sources.length > 0 &&
    sources.every(
      (source) =>
        stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills),
    )
  );
}

/** Where the draft's work stands, as live's progress line names it. */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  planning: boolean;
  drawing: boolean;
}

/** The stills of the draft's stage: the cast's at the cast, the keyframes' at the keyframes. */
export function stageStills(draft: StreetInterviewAdDraft): readonly StreetDraftStillName[] {
  return draft.stage === "keyframes" ? KEYFRAME_STILLS : CAST_STILLS;
}

export function workOf(draft: StreetInterviewAdDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    reading: draft.pack_director === "working",
    writing: draft.script_director === "working" || (answering && draft.pack_director !== "working"),
    planning: draft.shot_director === "working" || (draft.stage === "cast" && draft.stills.length === 0),
    drawing: stageStills(draft).some((name) => stillWaiting(name, draft.stills)),
  };
}

function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.planning || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: StreetInterviewAdDraft): DraftForm {
  const inputs = StreetInterviewAdInputs.safeParse(draft.inputs);
  return {
    inputs: inputs.success ? inputs.data : StreetInterviewAdInputs.parse({}),
    script: draft.script,
  };
}

/** The empty form: live's defaults, no product and no script (Reuse example off, live's
 * `form.reset`), as an empty draft reads back. */
export function emptyForm(): DraftForm {
  return { inputs: { ...StreetInterviewAdInputs.parse({}), product: null }, script: null };
}

/**
 * The card's example as the form takes it (live's `buildExampleValues`, G14 Street Interview-13):
 * the Overdrive run's picks, its packshot the card's own file typed like an upload, and its
 * approved script; null for no example of this template.
 */
export function exampleForm(example: TemplateExample | undefined): DraftForm | null {
  if (example?.template !== "street-interview-ad") return null;
  const { template: _template, script, ...picks } = example;
  const inputs = StreetInterviewAdInputs.safeParse(picks);
  return inputs.success ? { inputs: inputs.data, script } : null;
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it, as Podcast Ad's (`podcast-draft.ts`): the
 * form shows the draft as read; edits in the script stage, nothing working, are kept with PUT a
 * moment after typing pauses; each ask answers with the draft, shown from then on.
 */
export function useStreetDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetStreetInterviewAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<DraftForm | null>(null);
  // A form shown but not written (Replicate's example, live's mount values): kept off the draft
  // until the member's first action, as live writes nothing before its first director.
  const [held, setHeld] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetStreetInterviewAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: StreetInterviewAdDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
    setHeld(false);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteStreetInterviewAdScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveStreetInterviewAdScript(asks);
  const castApproval = useApproveStreetInterviewAdCast(asks);
  const regeneration = useRegenerateStreetInterviewAdStill(asks);
  const reset = useResetStreetInterviewAdDraft(asks);
  const asking =
    script.isPending ||
    approval.isPending ||
    castApproval.isPending ||
    regeneration.isPending ||
    reset.isPending;
  const work: DraftWork =
    draft === undefined
      ? { reading: false, writing: false, planning: false, drawing: false }
      : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteStreetInterviewAdDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: StreetInterviewAdDraftWriteOutput } | null>(
    null,
  );
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writeStreetInterviewAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable || held) return undefined;
    const body = StreetInterviewAdDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, held, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    editable,
    failure,
    asking,
    approving: approval.isPending || castApproval.isPending,
    resetting: reset.isPending,
    /** The form is shown but not written (`show`): nothing has been written since. */
    held,
    edit(change: Partial<DraftForm>) {
      if (server === null) return;
      setHeld(false);
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    /** The form shown, not written: the draft takes it with the member's first edit or ask. */
    show(shown: DraftForm) {
      if (server === null) return;
      setHeld(true);
      setLocal(shown);
    },
    /** The form shown dropped unwritten: the draft shows again as read (G14-street-reuse-notes). */
    discard() {
      setHeld(false);
      setLocal(null);
    },
    editInputs(change: Partial<StreetInterviewAdInputsOutput>) {
      if (server === null) return;
      setHeld(false);
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = StreetInterviewAdScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = StreetInterviewAdApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    approveCast() {
      ask();
      castApproval.mutate({ teamId, brandId });
    },
    regenerate(name: StreetDraftStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
